import React, { useRef, useState, useEffect } from 'react';
import { Tooltip, notification } from 'antd';
import logo from '../../../images/gigg-logo.svg';
import smallLogo from '../../../images/gigg-logo-single.svg';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import type { OrgMember } from '../../../models/Org';
import Modal from 'antd/lib/modal';
import OrgSwitcher from '../OrgSwitcher';
import CreateOrg from '../CreateOrg';
import { EntityNav, Roles, EntityTypes, EntitySubTypes } from '../../../constants';
import MobileNav from '../MobileNav';
import './Sidebar.scss';
import useUser, { useOrgMember } from '../../../services/hooks/useUser';
import useOrg, { useOrgConnections } from '../../../services/hooks/useOrg';
import type { OrgParams } from '../../../App';
import useEntity, { useEntityMembers } from '../../../services/hooks/useEntity';
import {
  getCookie,
  setCookie,
  getAliasFromType,
  getCompetitionAdvancementType,
  getCurrentStageNum,
  getTypeFromAlias,
} from '../../../utils/Utils';
import { useMediaQuery } from 'react-responsive';
import ProtectedElement from '../ProtectedElement';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EntityTypeGuard from '../EntityTypeGuard';
import BoardNav from '../BoardNav';
import AccountMenu from '../AccountMenu';
import { signOut } from 'aws-amplify/auth';
import {
  faChartLine,
  faCog,
  faComment,
  faFileContract,
  faGripHorizontal,
  faInbox,
  faWandMagicSparkles,
  faSignOut,
  faSitemap,
  faThumbtack,
  faUserCrown,
  faUserShield,
} from '@fortawesome/pro-light-svg-icons';
import CreateEntity from '../CreateEntity';

export interface State {
  accountMenuVisible: false;
  createOrgVisible: false;
}

const Pinned = (props: any) => {
  if (props.currentEntity.type === EntityTypes.COMPETITION && props.isSimpleCompetition) {
    return null;
  }

  return (
    <NavLink
      to={props.to}
      className={({ isActive }) => classNames('gigg-menu-link', { 'gigg-menu-link--active': isActive })}
      onClick={props.onClick}
    >
      <li className="gigg-menu__item">
        <FontAwesomeIcon icon={faThumbtack} className="gigg-menu__icon" fixedWidth />
        <span>{EntityNav[props.currentEntity.type].PINNED.label}</span>{' '}
        <span className="gigg-menu__count">{props.count}</span>
      </li>
    </NavLink>
  );
};

export interface MenuItemProps {
  path: string;
  name?: string;
  onClick?: () => void;
  children: any;
}

const MenuItem: React.FC<MenuItemProps> = ({ path, name = '', children, onClick }) => {
  const pathArray = path.split('/');
  const active = name === pathArray[pathArray.length - 1];
  const classes = classNames('gigg-menu__item', { 'gigg-menu__item--active': active });

  return (
    <li className={classes} onClick={onClick}>
      {children}
    </li>
  );
};

const Sidebar = () => {
  const { orgPath = '', entityId = '', entityType = '' } = useParams<OrgParams>();
  const [accountMenuVisible, setAccountMenuVisible] = useState(false);
  const [orgSwitcherVisible, setOrgSwitcherVisible] = useState(false);
  const [createOrgVisible, setCreateOrgVisible] = useState(false);
  const accountMenuRef = useRef<HTMLUListElement>(null);
  const { data: user } = useUser();
  const { data: currentOrg } = useOrg({ orgPath });
  const currentMember = useOrgMember({ orgPath });
  const { data: currentEntity } = useEntity({ entityId, entityType: getTypeFromAlias(entityType) });
  const { data: entityMembers } = useEntityMembers({ entityId, entityType: getTypeFromAlias(entityType) });
  const isMobile = useMediaQuery({ query: '(max-width: 823px)' });
  const entityMembership = entityMembers?.find((member: OrgMember) => member._id === user?._id);
  const navigation = useNavigate();
  const currentStageNum = getCurrentStageNum(currentEntity);

  useOrgConnections(orgPath, true, (connections) => {
    if (
      getCookie('socialConnectionWarning') !== 'dismissed' &&
      connections.some((connection) => {
        return !!connection.metadata?.error
      })
    )
      notification.warning({
        key: 'social-connection-error',
        message: 'Social Connections Error',
        description: (
          <div>
            Your organization’s connection to one or more social accounts is having issues, posts can no longer be pulled
            from these accounts. Click
            <a href={`${window.location.origin}/${orgPath}/settings`}> here</a> to review your connections.
          </div>
        ),
        duration: 0,
        onClose: () => {
          setCookie('socialConnectionWarning', 'dismissed', 1440);
        },
      });
  });

  useEffect(() => {
    if (user?.orgs) {
      setCreateOrgVisible(!user.orgs?.length);
    }
  }, [user]);

  // TODO: This should only run if the entity is a competition
  const { isSimpleCompetition } = getCompetitionAdvancementType(currentEntity);
  const entitySubType = currentEntity?.metadata?.submissionType;
  const totalStories = currentEntity?.stages?.stage0?.postCount || 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      const accountMenu = document.getElementById('accountMenu');
      if (accountMenu && !accountMenu.contains(event.target)) {
        setAccountMenuVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [accountMenuVisible]);

  const handleAccountMenuClick = () => {
    setAccountMenuVisible(!accountMenuVisible);
  };

  const handleOrgSwitchClick = () => {
    setOrgSwitcherVisible(true);
    setAccountMenuVisible(false);
  };

  const handleSignoutClick = async () => {
    await signOut();
    navigation('/login');
  };

  const handleHelpClick = () => {
    (window as any).HubSpotConversations.widget.load();
    (window as any).HubSpotConversations.widget.open();
  };

  const handleCreateOrgClick = () => {
    setOrgSwitcherVisible(false);
    setCreateOrgVisible(true);
  };

  const handleOrgSwitcherCancel = () => {
    setOrgSwitcherVisible(false);
  };

  const handleCreateOrgCancel = () => {
    setCreateOrgVisible(false);
  };

  return (
    <div className="sidebar">
      <div className="sidebar-content">
        {isMobile && (
          <MobileNav
            user={user}
            orgPath={orgPath}
            entityId={entityId}
            currentEntity={currentEntity}
            orgSwitchClick={handleOrgSwitchClick}
            accountMenuClick={handleAccountMenuClick}
            signOutClick={handleSignoutClick}
          />
        )}

        {!isMobile && (
          <>
            <NavLink to={`/${orgPath}`} className="sidebar-logo">
              <img src={logo} alt="Gigg" />
              <img src={smallLogo} className="sidebar-logo__small" alt="Gigg" />
            </NavLink>
            <ul className="gigg-menu gigg-menu__first">
              <ProtectedElement roles={[Roles.ADMIN]} member={currentMember}>
                <li className="gigg-menu__button">
                  <CreateEntity />
                </li>
              </ProtectedElement>

              <NavLink
                to={`/${orgPath}`}
                end
                className={({ isActive }) => classNames('gigg-menu-link', { 'gigg-menu-link--active': isActive })}
              >
                <li className="gigg-menu__item">
                  <FontAwesomeIcon icon={faGripHorizontal} className="gigg-menu__icon" fixedWidth />
                  <span>Dashboard</span>
                </li>
              </NavLink>
            </ul>
            {currentEntity && (
              <ul className="gigg-menu gigg-menu__second">
                <div className="gigg-menu__header">
                  <h3 className="gigg-menu__heading">{currentEntity.name}</h3>
                  {currentEntity.metadata?.autoPin && (
                    <Tooltip title="Auto-pinning is active" placement="bottom">
                      <FontAwesomeIcon icon={faWandMagicSparkles} className="text-success" />
                    </Tooltip>
                  )}
                </div>

                <NavLink
                  end
                  to={`/${orgPath}/${getAliasFromType(currentEntity.type)}/${entityId}`}
                  className={({ isActive }) => classNames('gigg-menu-link', { 'gigg-menu-link--active': isActive })}
                >
                  <li className="gigg-menu__item">
                    <FontAwesomeIcon icon={faInbox} className="gigg-menu__icon" fixedWidth />
                    <span>{EntityNav[currentEntity.type].INBOX.label}</span>
                    <EntityTypeGuard types={[EntityTypes.COMPETITION]}>
                      <span className="gigg-menu__count">{(totalStories || '').toLocaleString()}</span>
                    </EntityTypeGuard>
                  </li>
                </NavLink>
                <EntityTypeGuard types={[EntityTypes.COMPETITION]}>
                  <Pinned
                    to={`/${orgPath}/${currentEntity.type}/${entityId}/${EntityNav[currentEntity.type].PINNED.route}${
                      currentEntity.type === EntityTypes.COMPETITION ? `/${currentStageNum + 1 || ''}` : ''
                    }`}
                    path={location.pathname}
                    count={currentEntity.addedCount}
                    currentEntity={currentEntity}
                    isSimpleCompetition={isSimpleCompetition}
                  />
                </EntityTypeGuard>
                <EntityTypeGuard
                  types={[EntityTypes.BOARD, EntityTypes.COMPETITION]}
                  subTypes={
                    entitySubType ? [EntitySubTypes.UPLOADS, EntitySubTypes.SOCIAL_SITE, EntitySubTypes.BRACKET] : undefined
                  }
                  currentSubType={entitySubType || undefined}
                >
                  <NavLink
                    to={`/${orgPath}/${getAliasFromType(currentEntity.type)}/${entityId}/analytics/gigg`}
                    className={({ isActive }) => classNames('gigg-menu-link', { 'gigg-menu-link--active': isActive })}
                  >
                    <li className="gigg-menu__item">
                      <FontAwesomeIcon icon={faChartLine} className="gigg-menu__icon" fixedWidth />
                      <span>{EntityNav[currentEntity.type].ANALYTICS.label}</span>
                    </li>
                  </NavLink>
                </EntityTypeGuard>
                <ProtectedElement roles={[Roles.ADMIN]} member={entityMembership} allow={user?.admin}>
                  <NavLink
                    to={`/${orgPath}/${getAliasFromType(currentEntity.type)}/${entityId}/settings/general`}
                    className={({ isActive }) => classNames('gigg-menu-link', { 'gigg-menu-link--active': isActive })}
                  >
                    <li className="gigg-menu__item">
                      <FontAwesomeIcon icon={faCog} className="gigg-menu__icon" fixedWidth />
                      <span>{EntityNav[currentEntity.type].SETTINGS.label}</span>
                    </li>
                  </NavLink>
                </ProtectedElement>
                <EntityTypeGuard pathname={location.pathname} types={[EntityTypes.BOARD]}>
                  <BoardNav />
                </EntityTypeGuard>
              </ul>
            )}
            {user && currentOrg && (
              <div className="secondary-menu">
                <AccountMenu
                  visible={accountMenuVisible}
                  onClick={handleAccountMenuClick}
                  userName={`${user.firstName} ${user.lastName}`}
                  orgName={currentOrg.name}
                  content={
                    <ul className="popover-nav" ref={accountMenuRef} id="accountMenu">
                      <li
                        className="gigg-menu__item"
                        onClick={() => {
                          handleOrgSwitchClick();
                        }}
                      >
                        <FontAwesomeIcon icon={faSitemap} className="gigg-menu__icon" fixedWidth />
                        <span>Switch Orgs</span>
                      </li>
                      <NavLink
                        to={`/${orgPath}/settings`}
                        className={({ isActive }) => classNames('gigg-menu-link', { 'gigg-menu-link--active': isActive })}
                      >
                        <MenuItem path={location.pathname} name="settings">
                          <FontAwesomeIcon icon={faCog} className="gigg-menu__icon" fixedWidth />
                          <span>Settings</span>
                        </MenuItem>
                      </NavLink>
                      {user?.admin && (
                        <NavLink
                          to={`/admin`}
                          className={({ isActive }) => classNames('gigg-menu-link', { 'gigg-menu-link--active': isActive })}
                        >
                          <li className="gigg-menu__item">
                            <FontAwesomeIcon icon={faUserCrown} className="gigg-menu__icon" fixedWidth />
                            <span>Admin Dashboard</span>
                          </li>
                        </NavLink>
                      )}
                      <li className="gigg-menu__item" onClick={handleHelpClick}>
                        <FontAwesomeIcon icon={faComment} className="gigg-menu__icon" fixedWidth />
                        <span>Help and Support</span>
                      </li>
                      <li className="gigg-menu__item">
                        <FontAwesomeIcon icon={faFileContract} className="gigg-menu__icon" fixedWidth />
                        <a href="https://www.gigg.com/terms" target="_blank" rel="noopener noreferrer nofollow">
                          Terms and Conditions
                        </a>
                      </li>
                      <li className="gigg-menu__item">
                        <FontAwesomeIcon icon={faUserShield} className="gigg-menu__icon" fixedWidth />
                        <a href="https://www.gigg.com/privacy" target="_blank" rel="noopener noreferrer nofollow">
                          Privacy Policy
                        </a>
                      </li>
                      <li className="gigg-menu__item" onClick={handleSignoutClick}>
                        <FontAwesomeIcon icon={faSignOut} className="gigg-menu__icon" fixedWidth />
                        <span>Sign Out</span>
                      </li>
                    </ul>
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
      <CreateOrg visible={createOrgVisible} closable={!!currentOrg} onCancel={handleCreateOrgCancel} />
      {user?.orgs && currentOrg && (
        <Modal open={orgSwitcherVisible} footer={null} title="Switch Organizations" onCancel={handleOrgSwitcherCancel}>
          <OrgSwitcher
            visible={orgSwitcherVisible}
            orgs={user?.orgs}
            currentOrgId={currentOrg._id}
            onCreateOrgClick={handleCreateOrgClick}
          />
        </Modal>
      )}
    </div>
  );
};

export default Sidebar;
